import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, LayoutDashboard, Shield } from "lucide-react";
import { BrandMark } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/routes";

/**
 * Public marketing landing at `/`. Guests can open without signing in.
 * Console product surfaces live under `/dashboard/*`.
 */
export function LandingPage() {
  return (
    <div className="landing-canvas min-h-screen text-text-primary">
      <header className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-5">
        <Link
          to={ROUTES.home}
          className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
          aria-label="TinyBase home"
        >
          <BrandMark />
          <span className="font-display text-sm font-semibold tracking-tight">
            TinyBase
          </span>
        </Link>
        <nav className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={ROUTES.docs}>Docs</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to={ROUTES.login}>Sign in</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to={ROUTES.signup}>
              Get started
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-6 pb-20 pt-10">
        <section className="max-w-2xl page-enter">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-label text-text-muted">
            Backend-as-a-Service
          </p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-text-primary sm:text-5xl">
            Multi-tenant data, auth, and APIs for your apps
          </h1>
          <p className="mt-5 text-base leading-relaxed text-text-secondary">
            TinyBase gives each project isolated data-plane access, hosted auth,
            and platform APIs—so product teams ship without running the control
            plane themselves.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" asChild>
              <Link to={ROUTES.signup}>Create account</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={ROUTES.dashboard}>
                <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden />
                Open dashboard
              </Link>
            </Button>
            <Button size="lg" variant="ghost" asChild>
              <Link to={ROUTES.docs}>
                <BookOpen className="mr-2 h-4 w-4" aria-hidden />
                Read the docs
              </Link>
            </Button>
          </div>
        </section>

        <section className="mt-16 grid gap-4 sm:grid-cols-3">
          {[
            {
              title: "Project console",
              body: "SQL, tables, permissions, storage, and observability under /dashboard.",
              icon: LayoutDashboard,
            },
            {
              title: "Hosted auth",
              body: "Portal sign-in, OAuth, and account center for app users—separate from the console.",
              icon: Shield,
            },
            {
              title: "Developer docs",
              body: "Integrate TinyBase into your apps—client setup, API keys, auth, and data APIs. Guests welcome.",
              icon: BookOpen,
            },
          ].map((card) => (
            <article
              key={card.title}
              className="card-surface rounded-lg p-5"
            >
              <card.icon
                className="mb-3 h-5 w-5 text-secondary"
                aria-hidden
              />
              <h2 className="font-display text-sm font-semibold text-text-primary">
                {card.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                {card.body}
              </p>
            </article>
          ))}
        </section>

        <p className="mt-12 text-sm text-text-muted">
          Already have an account?{" "}
          <Link
            className="font-medium text-secondary underline-offset-4 hover:underline"
            to={ROUTES.login}
          >
            Sign in to the dashboard
          </Link>
          .
        </p>
      </main>
    </div>
  );
}
